import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { addDays, parseISO } from 'date-fns';
import bolService from '../services/bolService';
import labelService from '../services/labelService';
import { usePermissions } from '../context/PermissionContext';
import PalletLabel from '../components/PalletLabel';
import PalletLabelPdf from '../components/PalletLabelPdf';
import { pdf } from '@react-pdf/renderer';

// Group BOL items by palletId, derive per-pallet data
function buildPallets(items) {
  const map = new Map();
  for (const item of items) {
    const key = item.palletId || `__no_pallet_${item.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  return Array.from(map.entries()).map(([palletId, rows], idx) => ({
    palletId,
    palletNumber: idx + 1,
    lotCodes: [...new Set(rows.map(r => r.lotCode).filter(Boolean))],
    products: [...new Set(rows.map(r => r.Product?.name || r.productName).filter(Boolean))],
    productRows: rows, // keep for shelf life lookup
    cases: rows.reduce((s, r) => s + (Number(r.quantityCases) || 0), 0),
    expirationDate: null, // filled in after lot code date resolution
  }));
}

export default function PalletLabelPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { companySettings } = usePermissions();

  const [bol, setBol] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoBase64, setLogoBase64] = useState(null);

  const companyName = companySettings?.name || '';
  const logoUrl = companySettings?.logoUrl || null;
  const companyAddress = [
    companySettings?.addressLine1,
    companySettings?.city,
    companySettings?.stateProvince,
    companySettings?.postalCode,
    companySettings?.country,
  ].filter(Boolean).join(', ');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [bolData, tpls] = await Promise.all([
        bolService.getBol(id),
        labelService.getTemplates(),
      ]);
      setBol(bolData);
      setTemplates(tpls);

      // Set default template
      const def = tpls.find(t => t.isDefault) || tpls[0];
      if (def) setSelectedTemplateId(String(def.id));

      // Build pallet groups
      const items = bolData.Items || [];
      const grouped = buildPallets(items);

      // Resolve production dates from lot codes
      const allLotCodes = [...new Set(items.map(i => i.lotCode).filter(Boolean))];
      let lotDateMap = {};
      if (allLotCodes.length > 0) {
        lotDateMap = await labelService.getLotCodeDates(allLotCodes);
      }

      // Compute expiration per pallet — prefer stored expirationDate on each BOL item;
      // fall back to productionDate + shelfLifeDays for items that don't have one set.
      // Use the earliest date across all items on the pallet.
      const withDates = grouped.map(p => {
        let earliest = null;
        for (const row of p.productRows) {
          let exp = null;
          if (row.expirationDate) {
            try { exp = parseISO(row.expirationDate); } catch { /* skip */ }
          } else {
            const shelf = row.Product?.shelfLifeDays;
            const productionDateStr = row.lotCode ? (lotDateMap[row.lotCode] ?? null) : null;
            if (shelf && productionDateStr) {
              try { exp = addDays(parseISO(productionDateStr), shelf); } catch { /* skip */ }
            }
          }
          if (exp && (!earliest || exp < earliest)) earliest = exp;
        }
        return { ...p, expirationDate: earliest ? earliest.toISOString() : null };
      });

      setPallets(withDates);
    } catch (e) {
      setError('Failed to load BOL data.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!logoUrl) return;
    labelService.getImageAsBase64(logoUrl)
      .then(setLogoBase64)
      .catch(() => setLogoBase64(null));
  }, [logoUrl]);

  const selectedTemplate = templates.find(t => String(t.id) === selectedTemplateId) || null;
  const totalPallets = pallets.length;

  const [printingSingleId, setPrintingSingleId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await pdf(
        <PalletLabelPdf
          pallets={pallets.map(p => ({ ...p, totalPallets }))}
          bol={bol}
          template={selectedTemplate}
          companyName={companyName}
          companyAddress={companyAddress}
          logoUrl={logoBase64 || logoUrl}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pallet-labels-${bol.bolNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrintOne = (palletId) => {
    setPrintingSingleId(palletId);
    setTimeout(() => {
      window.print();
      setPrintingSingleId(null);
    }, 100);
  };

  if (loading) return <div className="container mt-4"><div className="spinner-border" /></div>;
  if (error) return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
  if (!bol) return null;

  return (
    <>
      {/* Screen controls — hidden on print */}
      <div className="no-print" style={{ padding: '16px 24px', borderBottom: '1px solid #dee2e6', background: '#f8f9fa', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/shipping/bol`)}>
          ← Back to BOL
        </button>
        <strong className="me-2">BOL #{bol.bolNumber} — Pallet Labels</strong>

        <div className="d-flex align-items-center gap-2">
          <label className="mb-0 small">Template:</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 220 }}
            value={selectedTemplateId}
            onChange={e => setSelectedTemplateId(e.target.value)}
          >
            {templates.length === 0 && <option value="">— No templates —</option>}
            {templates.map(t => (
              <option key={t.id} value={String(t.id)}>{t.name}{t.isDefault ? ' (default)' : ''}</option>
            ))}
          </select>
        </div>

        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-outline-success btn-sm" onClick={handleDownloadPdf} disabled={pdfLoading}>
            {pdfLoading ? 'Generating…' : '⬇ Download PDF'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={handlePrint}>
            🖨 Print
          </button>
        </div>
      </div>

      {/* Label print area */}
      <div id="pallet-labels-print-area" style={{ padding: 24, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        {pallets.map(p => {
          const excluded = printingSingleId && printingSingleId !== p.palletId;
          // Only the last VISIBLE label should skip the forced page-break-after —
          // otherwise Chrome's print engine emits a trailing blank sheet after it.
          const visibleIds = printingSingleId ? [printingSingleId] : pallets.map(x => x.palletId);
          const isLastPage = !excluded && p.palletId === visibleIds[visibleIds.length - 1];
          return (
            <div key={p.palletId} className={excluded ? 'exclude-print' : ''} style={{ position: 'relative' }}>
              <button
                className="no-print"
                title="Print this label"
                onClick={() => handlePrintOne(p.palletId)}
                style={{
                  position: 'absolute', top: 6, right: 6, zIndex: 10,
                  background: 'rgba(255,255,255,0.85)', border: '1px solid #ccc',
                  borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontSize: 14,
                }}
              >
                🖨
              </button>
              <PalletLabel
                pallet={{ ...p, totalPallets }}
                bol={bol}
                template={selectedTemplate}
                companyName={companyName}
                companyAddress={companyAddress}
                logoUrl={logoUrl}
                isLastPage={isLastPage}
              />
            </div>
          );
        })}

        {pallets.length === 0 && (
          <div className="text-muted">No pallets found on this BOL.</div>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: 6in 4in; margin: 0.1in; }
          .no-print { display: none !important; }
          html, body { margin: 0; padding: 0; }
          #pallet-labels-print-area {
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          #pallet-labels-print-area > div {
            /* page-break-after is set per-label in PalletLabel.js (skipped on the
               last visible one) — forcing it here too would override that and
               produce a trailing blank sheet after the last label. */
            page-break-inside: avoid;
            margin: 0 !important;
          }
          #pallet-labels-print-area > div.exclude-print {
            display: none !important;
          }
          #pallet-labels-print-area > div > div {
            box-sizing: border-box;
            width: 5.8in !important;
            height: 3.75in !important;
            overflow: hidden;
          }
        }
      `}</style>
    </>
  );
}
